import { useEffect, useRef } from "react";
import * as echarts from "echarts/core";
import { LineChart, BarChart, ScatterChart, SankeyChart, HeatmapChart, GraphChart, LinesChart, CustomChart } from "echarts/charts";
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  MarkLineComponent,
  VisualMapComponent,
  DatasetComponent,
  GraphicComponent,
} from "echarts/components";
import { SVGRenderer } from "echarts/renderers";
import type { EChartsOption } from "echarts";

echarts.use([
  LineChart,
  BarChart,
  ScatterChart,
  SankeyChart,
  HeatmapChart,
  GraphChart,
  LinesChart,
  CustomChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  MarkLineComponent,
  VisualMapComponent,
  DatasetComponent,
  GraphicComponent,
  SVGRenderer,
]);

interface ChartProps {
  option: EChartsOption;
  ariaLabel: string;
  height?: number;
  onPointHover?: (dataIndex: number | null) => void;
  onPointSelect?: (dataIndex: number) => void;
  selectAnySeries?: boolean;
  selectNodesOnly?: boolean;
}

export function Chart({ option, ariaLabel, height = 459, onPointHover, onPointSelect, selectAnySeries = false, selectNodesOnly = false }: ChartProps) {
  const ref = useRef<HTMLDivElement>(null);
  const instanceRef = useRef<echarts.ECharts | null>(null);

  const enforceCoverCrop = () => {
    ref.current?.querySelectorAll("svg image").forEach((image) => {
      image.setAttribute("preserveAspectRatio", "xMidYMid slice");
    });
  };

  useEffect(() => {
    if (!ref.current) return;
    const instance = echarts.init(ref.current, undefined, { renderer: "svg" });
    instanceRef.current = instance;
    const resize = () => instance.resize();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(resize) : null;
    // Hover and selection can add a short-lived emphasis SVG image after the
    // initial option render. Keep every replacement image on the same cover
    // crop rule as its resting node.
    const imageObserver = typeof MutationObserver !== "undefined"
      ? new MutationObserver(enforceCoverCrop)
      : null;
    observer?.observe(ref.current);
    imageObserver?.observe(ref.current, { childList: true, subtree: true });
    window.addEventListener("resize", resize);
    return () => {
      observer?.disconnect();
      imageObserver?.disconnect();
      window.removeEventListener("resize", resize);
      instance.dispose();
      instanceRef.current = null;
    };
  }, []);

  useEffect(() => {
    // Evidence charts should render as a stable snapshot. ECharts animates line
    // paths separately from their symbols, which can briefly make points appear
    // detached after a reload or resize.
    instanceRef.current?.setOption({ ...option, animation: false }, { notMerge: true });
    // ECharts' SVG image renderer defaults to `meet`, which letterboxes a
    // portrait game cover inside its square node bounds.  The competitive map
    // clips that square to a circle, so `slice` gives it the expected
    // object-fit: cover treatment: the full circle is artwork, not padding.
    queueMicrotask(enforceCoverCrop);
  }, [option]);

  useEffect(() => {
    const instance = instanceRef.current;
    if (!instance) return;
    const pointIndex = (event: unknown) => {
      const value = event as { seriesIndex?: number; dataIndex?: number; dataType?: string; data?: { sourceIndex?: number } };
      const selectable = selectAnySeries ? typeof value.seriesIndex === "number" : value.seriesIndex === 1;
      if (!selectable || (selectNodesOnly && value.dataType !== "node") || typeof value.dataIndex !== "number") return null;
      // A chart can render a selected point in a foreground series. Preserve
      // its original dataset index so click handlers still resolve the right row.
      return typeof value.data?.sourceIndex === "number" ? value.data.sourceIndex : value.dataIndex;
    };
    const onMouseOver = (event: unknown) => {
      const index = pointIndex(event);
      if (index !== null) onPointHover?.(index);
    };
    const onMouseOut = (event: unknown) => {
      if (pointIndex(event) !== null) onPointHover?.(null);
    };
    const onClick = (event: unknown) => {
      const index = pointIndex(event);
      if (index !== null) onPointSelect?.(index);
    };
    instance.on("mouseover", onMouseOver);
    instance.on("mouseout", onMouseOut);
    instance.on("click", onClick);
    return () => {
      instance.off("mouseover", onMouseOver);
      instance.off("mouseout", onMouseOut);
      instance.off("click", onClick);
    };
  }, [onPointHover, onPointSelect, selectAnySeries]);

  return (
    <div
      ref={ref}
      className="chart"
      style={{ height }}
      role="img"
      aria-label={ariaLabel}
      data-renderer="svg"
    />
  );
}
